import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  LinearProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { TTag } from 'api/tags/tags.types';
import { TTagsTable } from './TagsTable.types';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
  deleteTagAsyncThunk,
  getTagsListAsyncThunk,
  toggleTagActiveAsyncThunk,
} from 'store/tags/tags.action';
import { resetTagsList } from 'store/tags/tags.slices';
import { Loader } from 'components';
import { useInitialLoaderCondition } from 'hooks/useInitialLoaderCondition';
import { useModals } from 'hooks/useModals';
import { MODALS } from 'store/contexts/ModalContext/modals';

export const TagsTable: TTagsTable = () => {
  const modals = useModals();
  const dispatch = useAppDispatch();
  const {
    tagsList: { data: tagsList, isLoading: isTagsLoading },
    createTag: { isLoading: isCreateTagLoading },
    deleteTag: { isLoading: isDeleteTagLoading },
    updateTag: { isLoading: isUpdateTagLoading },
    toggleTagActive: { isLoading: isToggleTagActiveLoading },
  } = useAppSelector((store) => store.tags);

  const isInitialLoadingFinished = useInitialLoaderCondition(isTagsLoading);

  const isLoading =
    isTagsLoading ||
    isCreateTagLoading ||
    isDeleteTagLoading ||
    isUpdateTagLoading ||
    isToggleTagActiveLoading;

  const [anchorEl, setAnchorEl] = React.useState<{
    [key: string]: HTMLElement | null;
  }>({});

  const loadTableItems = React.useCallback(() => {
    dispatch(getTagsListAsyncThunk());
  }, [dispatch]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    itemId: string
  ) => {
    setAnchorEl({ [itemId]: event.currentTarget });
  };

  const handleMenuClose = (itemId: string) => {
    setAnchorEl((prev) => ({ ...prev, [itemId]: null }));
  };

  const handleEdit = (item: TTag) => {
    handleMenuClose(item.id);
    modals.openModal(MODALS.ADMIN_TAG_EDIT, { item });
  };

  const handleDelete = (item: TTag) => {
    handleMenuClose(item.id);
    dispatch(deleteTagAsyncThunk({ id: item.id, onSuccess: loadTableItems }));
  };

  const handleToggleActive = (item: TTag) => {
    dispatch(
      toggleTagActiveAsyncThunk({
        id: item.id,
        onSuccess: loadTableItems,
      })
    );
  };

  React.useEffect(() => {
    loadTableItems();
    return () => {
      dispatch(resetTagsList());
    };
  }, []);

  if (!isInitialLoadingFinished) return <Loader />;

  return (
    <div className="entity-table">
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ position: 'relative' }}>
              <TableCell className="entity-table__cell entity-table__cell--switch">
                Active
              </TableCell>
              <TableCell className="entity-table__cell">Name</TableCell>
              <TableCell className="entity-table__cell">Color</TableCell>
              <TableCell className="entity-table__cell entity-table__cell--actions">
                Actions
              </TableCell>
              {isLoading && (
                <LinearProgress
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                  }}
                />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tagsList?.items?.map((item) => (
              <TableRow key={item.id} className="entity-table__row">
                <TableCell className="entity-table__cell entity-table__cell--switch">
                  <Switch
                    checked={item.isActive}
                    onChange={() => handleToggleActive(item)}
                    size="small"
                  />
                </TableCell>
                <TableCell className="entity-table__cell">
                  {item.name}
                </TableCell>
                <TableCell className="entity-table__cell">
                  <div className="entity-table__color-preview">
                    <div
                      className="entity-table__color-preview-box"
                      style={{ backgroundColor: item.color || '#000000' }}
                    />
                    <span>{item.color || 'No color'}</span>
                  </div>
                </TableCell>
                <TableCell className="entity-table__cell entity-table__cell--actions">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, item.id)}
                    className="entity-table__menu-button"
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl[item.id]}
                    open={Boolean(anchorEl[item.id])}
                    onClose={() => handleMenuClose(item.id)}
                  >
                    <MenuItem
                      onClick={() => handleEdit(item)}
                      className="entity-table__menu-item"
                    >
                      <EditIcon fontSize="small" sx={{ mr: 1 }} />
                      Edit
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleDelete(item)}
                      className="entity-table__menu-item entity-table__menu-item--delete"
                    >
                      <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      Delete
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
